import { useMemo } from "react";
import { useAllActiveOffices } from "repositories/scheduler-repository";
import useEffectiveOwnerId from "hooks/useEffectiveOwnerId";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

export const OFFICE_CONTEXT_ALL_ACTIVE_PAGE_SIZE = 100;

/**
 * Request params for Office/AllActive when a module is opened from a selected office.
 * @param {number} ownerId
 */
export function getOfficeContextAllActiveParams(ownerId) {
  return {
    ownerId,
    includeAdminOffice: false,
    includeOwnerOffices: true,
  };
}

/**
 * Request params for Office/AllActive in Office Policy "Assigned to you" view.
 * @param {number|null|undefined} [ownerId]
 */
export function getAssignedToYouAllActiveParams(ownerId) {
  return {
    ...(ownerId != null && ownerId !== "" ? { ownerId } : {}),
    includeAdminOffice: false,
    includeOwnerOffices: false,
  };
}

/**
 * OwnerId for Assigned To You API calls: sent when logged in as owner, null for staff.
 * @param {boolean} isOwner
 * @param {number|null|undefined} effectiveOwnerId
 * @returns {number|null}
 */
export function resolveAssignedToYouOwnerId(isOwner, effectiveOwnerId) {
  return isOwner ? effectiveOwnerId ?? null : null;
}

/**
 * Office list for Notes/Task modules opened from a selected office.
 * Uses Office/AllActive with includeAdminOffice=false and includeOwnerOffices=false.
 *
 * @param {number} [pageSize=100]
 */
export function useOfficeNavigationModuleOfficeList(
  pageSize = OFFICE_CONTEXT_ALL_ACTIVE_PAGE_SIZE,
  { enabled: hookEnabled = true } = {},
) {
  const { isOwner } = useAccountOwnerCommon();
  const { isOfficeNavigationContext, effectiveOwnerId } = useEffectiveOwnerId();
  const moduleOwnerId = resolveAssignedToYouOwnerId(isOwner, effectiveOwnerId);

  const { data, isLoading, isFetching } = useAllActiveOffices(
    1,
    pageSize,
    getAssignedToYouAllActiveParams(moduleOwnerId),
    "",
    {
      enabled: hookEnabled && isOfficeNavigationContext,
    },
  );

  const officeList = useMemo(() => {
    if (!isOfficeNavigationContext || !data?.data?.length) return [];
    return data.data
      .map((office) => ({
        id: office.id ?? office.officeId,
        name: office.name ?? office.officeName ?? "",
      }))
      .filter((office) => office.id != null);
  }, [isOfficeNavigationContext, data]);

  return {
    isOfficeNavigationContext,
    officeList,
    moduleOwnerId,
    isLoading: isOfficeNavigationContext && (isLoading || isFetching),
  };
}

/**
 * Loads offices via Office/AllActive for modules opened from a selected office context.
 * Returns an empty list and skips the query when opened from the global profile flow.
 *
 * @param {number} [pageSize=100]
 */
export default function useOfficeContextOfficeList(
  pageSize = OFFICE_CONTEXT_ALL_ACTIVE_PAGE_SIZE,
  { enabled: hookEnabled = true } = {},
) {
  const { isOfficeNavigationContext, officeContext } = useEffectiveOwnerId();
  const contextOwnerId = officeContext?.ownerId;

  const { data, isLoading, isFetching } = useAllActiveOffices(
    1,
    pageSize,
    contextOwnerId ? getOfficeContextAllActiveParams(contextOwnerId) : null,
    "",
    {
      enabled:
        hookEnabled && isOfficeNavigationContext && !!contextOwnerId,
    },
  );

  const officeList = useMemo(() => {
    if (!isOfficeNavigationContext || !data?.data?.length) return [];
    return data.data
      .map((office) => ({
        id: office.id ?? office.officeId,
        name: office.name ?? office.officeName ?? "",
      }))
      .filter((office) => office.id != null);
  }, [isOfficeNavigationContext, data]);

  return {
    isOfficeNavigationContext,
    officeList,
    isLoading: isOfficeNavigationContext && (isLoading || isFetching),
  };
}
